import { Input } from 'src/lib/constants';
import {
  determineIfAboveAverageCredit,
  determineIfPoorCredit,
} from 'src/lib/credit-utils';
import { InputsReducer } from 'src/redux/reducers/inputs';
import { Screen } from 'src/lib/flows/screens';
import { CreditScore, PrimaryGoal } from 'src/types/inputs';
import { QueryParams } from 'src/redux/reducers/query-params';
import { ExperimentsReducer } from 'src/redux/reducers/experiments';
import {
  isNewBigPurchaseFlowExperimentActive,
  isNewBuildCreditFlowExperimentActive,
  isNewBalanceTransferFlowExperimentActive,
} from '../experiments';

export const NextStep = (
  experiments: ExperimentsReducer
): Record<PrimaryGoal, Screen> => ({
  [PrimaryGoal.Travel]: Screen.InternationalTravel,
  [PrimaryGoal.BigPurchase]: isNewBigPurchaseFlowExperimentActive(experiments)
    ? Screen.IntroAPROrRewards
    : Screen.HowLongToPayOffDebt,
  [PrimaryGoal.BuildCredit]: isNewBuildCreditFlowExperimentActive(experiments)
    ? Screen.CollegeStudent
    : Screen.CreditHistory,
  [PrimaryGoal.CashBack]: Screen.CashBackPreference,
  [PrimaryGoal.Debt]: isNewBalanceTransferFlowExperimentActive(experiments)
    ? Screen.AddDebtScreen
    : Screen.IsDebtCreditCard,
});

export const determineScreenAfterPrimaryGoal = ({
  primaryGoal,
  experiments,
}: {
  primaryGoal: PrimaryGoal;
  experiments: ExperimentsReducer;
}): Screen => {
  return NextStep(experiments)[primaryGoal];
};

export const determineScreenAfterCreditScore = ({
  inputs,
  queryParams,
  experiments,
}: {
  inputs: InputsReducer;
  queryParams: QueryParams;
  experiments: ExperimentsReducer;
}): Screen => {
  const creditScore = inputs[Input.CreditScore];
  const hasPoorCredit = creditScore === CreditScore.Poor;
  const hasAverageCredit = creditScore === CreditScore.Average;
  const isLimitedCreditHistory =
    creditScore === CreditScore.LimitedCreditHistory;

  if (isLimitedCreditHistory) {
    return Screen.CollegeStudent;
  }

  if (hasPoorCredit) {
    if (isNewBuildCreditFlowExperimentActive(experiments)) {
      return Screen.SecurityDeposit;
    }
    return NextStep(experiments)[PrimaryGoal.BuildCredit];
  }

  const primaryGoalQueryParam = queryParams?.[Input.PrimaryGoal];

  if (primaryGoalQueryParam) {
    if (primaryGoalQueryParam === PrimaryGoal.BuildCredit) {
      if (hasAverageCredit) {
        return NextStep(experiments)[PrimaryGoal.BuildCredit];
      }
      return Screen.PrimaryGoal;
    }
    return determineScreenAfterPrimaryGoal({
      primaryGoal: primaryGoalQueryParam as PrimaryGoal,
      experiments,
    });
  }

  return Screen.PrimaryGoal;
};

export const determineInitialScreen = ({
  isReturnUser,
  queryParams,
  experiments,
}: {
  queryParams: QueryParams;
  isReturnUser: boolean;
  experiments: ExperimentsReducer;
}): Screen => {
  if (isReturnUser) return Screen.WelcomeBack;

  const primaryGoalQueryParam = queryParams?.[Input.PrimaryGoal];
  const creditScoreQueryParam = queryParams?.[Input.CreditScore];

  const hasPoorCredit = determineIfPoorCredit({
    creditScoreQueryParam,
  });

  if (creditScoreQueryParam) {
    if (hasPoorCredit) return NextStep(experiments)[PrimaryGoal.BuildCredit];
    if (primaryGoalQueryParam) {
      if (
        primaryGoalQueryParam === PrimaryGoal.BuildCredit &&
        determineIfAboveAverageCredit({
          creditScoreQueryParam,
        })
      ) {
        return Screen.PrimaryGoal;
      }
      return determineScreenAfterPrimaryGoal({
        primaryGoal: primaryGoalQueryParam as PrimaryGoal,
        experiments,
      });
    }
    return Screen.PrimaryGoal;
  }

  return Screen.CreditScore;
};
